import { createAsyncThunk, unwrapResult } from '@reduxjs/toolkit';
import Router from 'next/router';
import qs from 'query-string';

import { myBooksApi } from '~/api/account/myBooksApi';
import {
  GetPlaceReadByBookSlugParams,
  GetPlaceReadResult,
  SavePlaceReadParams,
} from '~/api/account/myBooksApiTypes';
import { BookIdType } from '~/api/book/bookApiTypes';
import { campaignsApi } from '~/api/finance/marketing/campaignsApi';
import { reactQueryClient } from '~/api/reactQuery';
import { pagesConfigStore } from '~/atomic/atom/links/pagesConfigStore';
import {
  bookFormatPageStepSelector,
  isBookFormatBookReaderSelector,
  isFirstBookFormatPageSelector, isFirstChapterInReaderSelector,
  isLastBookFormatPageSelector, isSecondChapterInReaderSelector,
  readerSelector,
} from '~/atomic/page/book-reader/reader.selectors';
import {
  bookReaderSliceName,
  readerActions,
  readerDefaultSettings,
  textSettingsName,
} from '~/atomic/page/book-reader/reader.slice';
import { getUserAbonnementCashName } from '~/atomic/page/bookSubscriptions/abonnementPage.data';
import { authSelector } from '~/feature/authorization/auth.selector';
import { getAuthTokenFromClient } from '~/feature/authorization/getAuthToken';
import { bookSelector } from '~/feature/book/book.selector';
import { openBuyBookModal } from '~/feature/book/buyBook/buyBook.data';
import { buyBookSelector } from '~/feature/book/buyBook/buyBook.selector';
import { getPublishedChapterListByBookSlugFromGraphql } from '~/feature/chapter/chapter.data';
import {
  chapterSelector,
  ebookChaptersSelector,
  lastPublishAvailableChapterSelector,
} from '~/feature/chapter/chapter.selector';
import { chapterActions } from '~/feature/chapter/chapter.slice';
import { defaultCycleName, getCyclesByAuthorUsername } from '~/feature/cycle/cycleSlice';
import { choosePaySelector } from '~/feature/finance/BuyingSteps/ChoosePay/choosePay.selector';
import { viewChapter } from '~/feature/statistics/viewChapter';
import { isLoggedInSelector } from '~/feature/user/isLoggedInSelector';
import { ym } from '~/feature/yandex/YandexMetrikaInit';
import { reachVkPixelGoalOnceForBook, ymOnceForBook } from '~/feature/yandex/ymOnceForBook';
import { booksGraphql, GetEBookChapterParams } from '~/graphql/books/booksGraphql';
import { GraphqlEChapter } from '~/graphql/books/factory/book/EBookChapterFactoryTypes';
import { GraphqlEChapterWithContent } from '~/graphql/books/factory/book/EChapterWithContentFactoryTypes';
import { environments } from '~/lib/const';
import { getLocalStorageSafe } from '~/lib/utils/getLocalStorageSafe';
import { logMessageToSentry } from '~/lib/utils/sentryLogger';
import { RootState } from '~/store';

export const NEXT_BOOK_FORMAT_PAGE_STEP = 1;

export const bookIdsForAddMyBooksName = 'bookIdsForAddMyBooks';

const VK_PIXEL_ID = 3266273;

export const reachVkPixelGoal = (goal: string) => {
  if (typeof window === 'undefined') return;
  const tmr = (window as any)._tmr;
  if (!tmr || typeof tmr.push !== 'function') return;

  tmr.push({
    type: 'reachGoal', id: VK_PIXEL_ID, goal,
  });
};

export const getReaderTextChapter = createAsyncThunk<GraphqlEChapterWithContent, GetEBookChapterParams & { bookSlug: string; skipModalOpen?: boolean }, {
  rejectValue: { error: string }, state: RootState
}>(
  `${bookReaderSliceName}/getTextChapter`,
  async (
    data,
    thunkAPI,
  ) => {
    const state = thunkAPI.getState();
    const { isLoggedInSelector } = await import('~/feature/user/isLoggedInSelector');
    const { currentChapter } = state.chapter;
    const { isRefreshAfterAuthLoading } = readerSelector(state);
    const { loading: authLoading } = state.auth;

    // Определяем способ авторизации (используется для OAuth-guard ниже)
    let authMethod = 'none';
    if (authLoading.providerCallback) authMethod = 'oauth';
    else if (authLoading.login) authMethod = 'email';
    else if (isRefreshAfterAuthLoading) authMethod = 'refresh-after-auth';

    // Определяем тип сценария
    let scenario = 'unknown';
    if (environments.isClient) {
      const urlParams = new URLSearchParams(window.location.search);
      // Используем get() вместо has() — пустые ключи (?code&provider) не должны
      // считаться OAuth-коллбеком. Поведение совпадает с router.query.code в компоненте.
      const hasCode = !!urlParams.get('code');
      const hasProvider = !!urlParams.get('provider');
      const hasSkipAfterLogin = urlParams.has('skipAfterLogin');

      if (hasCode && hasProvider) scenario = 'oauth-redirect';
      else if (hasSkipAfterLogin) scenario = 'after-auth-navigation';
      else if (!currentChapter) scenario = 'initial-load';
      else if (currentChapter.chapterId === data.chapterId) scenario = 'page-refresh';
      else if (currentChapter.chapterId < data.chapterId) scenario = 'next-chapter';
      else if (currentChapter.chapterId > data.chapterId) scenario = 'previous-chapter';
      else scenario = 'chapter-navigation';
    }

    try {
      thunkAPI.dispatch(chapterActions.changeIsCurrentChapterLoading(true));
      const result = await booksGraphql.getEbookChapter({ chapterId: data.chapterId, token: getAuthTokenFromClient() });

      if (result.available === false) {
        // Если skipModalOpen=true, значит вызов идёт из providerCallback, который УЖЕ перезагрузил все данные
        // Не нужно делать retry повторно — диспатчим результат и возвращаемся
        if (data.skipModalOpen) {
          thunkAPI.dispatch(chapterActions.changeCurrentChapter(result));
          return result;
        }

        // НЕ диспатчим changeCurrentChapter сразу — иначе React отрендерит экран «нет доступа»
        // до того, как retry успеет исправить ситуацию (ложное срабатывание Sentry + мерцание UI).
        // Сначала пытаемся восстановить доступ, и только потом обновляем state.
        reactQueryClient.removeQueries(getUserAbonnementCashName);

        // Проверяем валидность токена: если думаем что авторизованы, проверяем через /me
        const stateBeforeCheck = thunkAPI.getState();
        const isLoggedInBeforeCheck = isLoggedInSelector(stateBeforeCheck);
        if (isLoggedInBeforeCheck) {
          try {
            const { me } = await import('~/feature/user/user.data');
            await thunkAPI.dispatch(me());
          } catch (error) {
            // me() сбросит isAuth при 401
          }
        }

        // Перезагружаем данные книги и списки глав, чтобы обновить флаг bought
        const { getGraphqlBook } = await import('~/feature/book/book.data');
        await thunkAPI.dispatch(getGraphqlBook({ slug: data.bookSlug }));
        await thunkAPI.dispatch(getPublishedChapterListByBookSlugFromGraphql({ slug: data.bookSlug, includeStats: false }));

        // Если книга подтверждена как купленная, повторно запрашиваем главу с актуальным токеном
        const stateAfterBookReload = thunkAPI.getState();
        const reloadedBook = stateAfterBookReload.book.currentGraphqlBook;
        if (reloadedBook?.ebook?.bought) {
          const retryToken = getAuthTokenFromClient();
          logMessageToSentry('bought_book_chapter_unavailable', {
            level: 'warning',
            tags: { feature: 'reader-access' },
            extra: {
              chapterId: data.chapterId,
              bookSlug: data.bookSlug,
              bookId: reloadedBook.id,
              hasToken: !!retryToken,
              scenario,
            },
          });
          const freshResult = await booksGraphql.getEbookChapter({ chapterId: data.chapterId, token: retryToken });
          thunkAPI.dispatch(chapterActions.changeCurrentChapter(freshResult));
          if (freshResult.available) {
            return freshResult;
          }
          logMessageToSentry('bought_book_chapter_still_unavailable_after_retry', {
            level: 'error',
            tags: { feature: 'reader-access' },
            extra: {
              chapterId: data.chapterId,
              bookSlug: data.bookSlug,
              bookId: reloadedBook.id,
              hasToken: !!retryToken,
              scenario,
            },
          });
        } else {
          // Книга не куплена — диспатчим оригинальный результат
          thunkAPI.dispatch(chapterActions.changeCurrentChapter(result));
        }

        // Проверяем состояние авторизации ПОСЛЕ перезагрузки книги/глав
        const stateAfterReload = thunkAPI.getState();
        const { loading: authLoadingAfterReload } = stateAfterReload.auth;
        const { isRefreshAfterAuthLoading } = readerSelector(stateAfterReload);
        const isProviderCallbackInProgressAfterReload = authLoadingAfterReload.providerCallback === true;

        if (isProviderCallbackInProgressAfterReload || isRefreshAfterAuthLoading) {
          return result;
        }

        // Если сценарий указывает на OAuth-редирект, не открываем модалку
        if (scenario === 'oauth-redirect' || authMethod === 'oauth') {
          return result;
        }

        if (environments.isClient) {
          const state = thunkAPI.getState();
          const isLoggedIn = isLoggedInSelector(state);
          const { authPreviously: authPreviouslyFromRedux, providerCallbackFailed } = authSelector(state);
          const storage = getLocalStorageSafe();
          const authPreviouslyFromStorage = storage?.getItem('auth_previously') === 'true';
          const authPreviously = authPreviouslyFromRedux || authPreviouslyFromStorage;
          const { isOpenModal: isBuyModalOpen } = buyBookSelector(state);
          const {
            isOpenQuickAuthModalWithChoosePayBook,
            isOpenQuickLoginModalWithChoosePayBook,
            isOpenQuickRegisterModalWithChoosePayBook,
          } = choosePaySelector(state);
          const {
            isOpenAuthModalWithLoadSaveChapter,
            isOpenLoginModalWithLoadSaveChapter,
            isOpenRegisterModalWithLoadSaveChapter,
            isRefreshAfterAuthLoading: isRefreshAfterAuth,
          } = readerSelector(state);

          let providerCallbackFailedFromStorage = false;
          try {
            providerCallbackFailedFromStorage = sessionStorage.getItem('provider_callback_failed') === 'true';
            if (providerCallbackFailedFromStorage) {
              sessionStorage.removeItem('provider_callback_failed');
            }
          } catch {
            // ignore sessionStorage errors
          }

          const urlParams = new URLSearchParams(window.location.search);
          const isOAuthCallbackInUrl = !!(urlParams.get('code') && urlParams.get('provider'));
          const isBuyBookInUrl = urlParams.get('buyBook') === 'true';
          const isSkipAfterLogin = urlParams.get('skipAfterLogin') === 'true';
          const isProviderCallbackFailed = providerCallbackFailed || providerCallbackFailedFromStorage;

          const isAnyAuthModalOpen = isOpenAuthModalWithLoadSaveChapter
            || isOpenLoginModalWithLoadSaveChapter
            || isOpenRegisterModalWithLoadSaveChapter
            || isOpenQuickAuthModalWithChoosePayBook
            || isOpenQuickLoginModalWithChoosePayBook
            || isOpenQuickRegisterModalWithChoosePayBook;

          if (
            !isBuyModalOpen
            && !isAnyAuthModalOpen
            && !isProviderCallbackFailed
            && !isOAuthCallbackInUrl
            && !isBuyBookInUrl
            && !isRefreshAfterAuth
            && !isSkipAfterLogin
          ) {
            if (isLoggedIn) {
              thunkAPI.dispatch(openBuyBookModal());
            } else if (authPreviously) {
              thunkAPI.dispatch(openBuyBookModal());
            } else {
              thunkAPI.dispatch(readerActions.setIsOpenAuthModalWithLoadSaveChapter(true));
            }
          }
        }
      } else {
        // Глава доступна — диспатчим сразу
        thunkAPI.dispatch(chapterActions.changeCurrentChapter(result));
      }

      // Отправляем событие при открытии последней бесплатной главы один раз за сессию
      try {
        const { currentBookProperties, currentGraphqlBook } = bookSelector(thunkAPI.getState());
        const chapters = ebookChaptersSelector(thunkAPI.getState());
        const { textSettings } = readerSelector(thunkAPI.getState());

        const lastFreeIdx = (currentBookProperties?.freeChaptersCount ?? 0) - 1;
        const lastFreeChapter = lastFreeIdx >= 0 ? chapters?.[lastFreeIdx] : undefined;
        const isLastFreeChapter = lastFreeChapter
          && Number(lastFreeChapter.chapterId) === Number(data.chapterId);

        if (environments.isClient && isLastFreeChapter && currentGraphqlBook?.id) {
          const goal = textSettings.isBookFormat ? 'reader-horizont' : 'reader-vertical';
          const sessionKey = `ym_last_free_chapter_${currentGraphqlBook.id}_${goal}`;

          // Проверяем, отправлялась ли цель в текущей сессии
          const alreadySent = sessionStorage.getItem(sessionKey);

          if (!alreadySent && typeof ym === 'function') {
            ym('reachGoal', goal);
            sessionStorage.setItem(sessionKey, 'true');
          }
        }
      } catch {
        // ignore goal errors
      }

      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    } finally {
      thunkAPI.dispatch(chapterActions.changeIsCurrentChapterLoading(false));
    }
  },
);

/**
 * Обработчик после успешной авторизации в читалке.
 * Перезагружает данные книги, проверяет доступность целевой главы,
 * и либо переходит на неё, либо показывает модалку покупки.
 */
export const handleReaderAfterAuth = createAsyncThunk<
void,
{ chapterId: string },
{ state: RootState }
>(
  `${bookReaderSliceName}/handleReaderAfterAuth`,
  async ({ chapterId }, thunkAPI) => {
    const { currentGraphqlBook } = bookSelector(thunkAPI.getState());
    const { clickedChapterId } = readerSelector(thunkAPI.getState());

    // Целевая глава: приоритет у clickedChapterId (на какую главу пользователь кликнул)
    const initialTargetChapterId = clickedChapterId || chapterId;
    const normalizedInitialTargetChapterId = initialTargetChapterId ? Number(initialTargetChapterId) : null;

    // АТОМАРНО устанавливаем все флаги для блокировки UI
    // Это гарантирует, что компонент увидит authTargetChapterId и isRefreshAfterAuthLoading одновременно
    thunkAPI.dispatch(readerActions.startAuthRefresh({ targetChapterId: normalizedInitialTargetChapterId }));

    try {
      // Перезагружаем данные книги с актуальным токеном
      const { getGraphqlBook } = await import('~/feature/book/book.data');
      const book = unwrapResult(await thunkAPI.dispatch(getGraphqlBook({ slug: currentGraphqlBook?.slug })));

      // Обновляем список глав (с учётом авторизации) чтобы получить актуальные available/paid флаги
      const { getPublishedChapterListByBookSlugFromGraphql } = await import('~/feature/chapter/chapter.data');
      await thunkAPI.dispatch(getPublishedChapterListByBookSlugFromGraphql({
        slug: currentGraphqlBook?.slug,
        includeStats: false,
      }));
      const refreshedChapters = ebookChaptersSelector(thunkAPI.getState());

      // Если target изменился после обновления состояния, фиксируем его
      const targetChapterId = clickedChapterId || chapterId;
      const normalizedTargetChapterId = targetChapterId ? Number(targetChapterId) : null;
      if (normalizedTargetChapterId !== normalizedInitialTargetChapterId) {
        thunkAPI.dispatch(readerActions.setAuthTargetChapterId(normalizedTargetChapterId));
      }

      if (normalizedTargetChapterId) {
        const chapterResult = unwrapResult(
          await thunkAPI.dispatch(getReaderTextChapter({
            chapterId: normalizedTargetChapterId,
            bookSlug: currentGraphqlBook?.slug,
            skipModalOpen: true, // Не показываем модалки при проверке доступности
          })),
        );

        // Если целевая глава стала доступна после авторизации — переходим на неё автоматически
        if (chapterResult?.available) {
          thunkAPI.dispatch(readerActions.changeClickedChapterId(null));

          // Очищаем buyBook из URL и добавляем skipAfterLogin, чтобы предотвратить повторный вызов checkOpenBuyBookModalFromUrl
          if (environments.isClient) {
            const urlParams = new URLSearchParams(window.location.search);
            const hadBuyBook = urlParams.get('buyBook') === 'true';
            const hadPage = urlParams.get('page');

            // Очищаем параметры покупки
            urlParams.delete('buyBook');
            urlParams.delete('payBookClickedChapterId');
            urlParams.delete('payBookPayEntity');

            // Очищаем параметры OAuth (code, provider) - они уже обработаны
            urlParams.delete('code');
            urlParams.delete('provider');

            // Добавляем skipAfterLogin, чтобы BuyBookModal не вызвал checkOpenBuyBookModalFromUrl
            // Этот параметр нужен только если был buyBook в URL
            if (hadBuyBook) {
              urlParams.set('skipAfterLogin', 'true');
            }

            // ВАЖНО: параметр page должен сохраниться!

            const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
            window.history.replaceState({}, '', newUrl);
          }

          // Используем ОБНОВЛЕННЫЕ главы из состояния (после авторизации)
          const targetChapter = refreshedChapters.find((ch) => ch.chapterId === normalizedTargetChapterId);

          // Напрямую обно��ляем URL через Router.replace, т.к. changeReaderChapter
          // сделает ранний return (currentChapterId уже обновлен через getReaderTextChapter выше)

          // Проверяем, является ли это переходом на другую главу или перезагрузкой той же главы
          const currentChapterIdFromUrl = Number(chapterId); // chapterId из параметра функции (из URL)
          const isNavigatingToNewChapter = normalizedTargetChapterId !== currentChapterIdFromUrl;

          // Устанавливаем флаг isChapterNavigation только если это переход на ДРУГУЮ главу
          // Если это перезагрузка той же главы (F5 на платной главе после слёта авторизации),
          // то НЕ устанавливаем флаг, чтобы позиция восстановилась из localStorage
          if (isNavigatingToNewChapter) {
            thunkAPI.dispatch(readerActions.setIsChapterNavigation(true));
          }

          // Сбрасываем скролл только при переходе на новую главу (не при перезагрузке)
          const isBookFormatMode = isBookFormatBookReaderSelector(thunkAPI.getState());
          if (isNavigatingToNewChapter && !isBookFormatMode && typeof window !== 'undefined' && window?.scrollTo) {
            window.scrollTo({ top: 0, behavior: 'auto' });
          }

          // Для режима листания добавляем ?page=0 в URL только при переходе на новую главу
          const pageParam = (isNavigatingToNewChapter && isBookFormatMode) ? '?page=0' : '';

          await Router.replace(
            pagesConfigStore.readerChapter.url,
            `${pagesConfigStore.readerChapter.as(currentGraphqlBook?.slug, normalizedTargetChapterId)}${pageParam}`,
            { shallow: true },
          );

          // НЕ сбрасываем isRefreshAfterAuthLoading здесь - это сделает ReaderTextChapter
          // когда увидит, что chapterId из URL совпадает с загруженной главой
          return;
        }
      }

      thunkAPI.dispatch(readerActions.setLoading(false));
      thunkAPI.dispatch(readerActions.setIsRefreshAfterAuthLoading(false));
      thunkAPI.dispatch(readerActions.setAuthTargetChapterId(null));
      // Если глава недоступна — показываем модалки покупки/абонемента
      const { afterLoginHandler } = await import('~/feature/book/buyBook/buyBook.data');
      const { userSelector } = await import('~/feature/user/user.selector');
      const { UserAbonnementStatusEnum } = await import('~/api/account/accountApiTypes');
      const { user } = userSelector(thunkAPI.getState());

      thunkAPI.dispatch(afterLoginHandler({
        bookId: book.id,
        bookBought: book.ebook.bought,
        haveUserAbonnement: user?.abonnement && user?.abonnement?.status === UserAbonnementStatusEnum.Active,
        bookAddedToAbonnement: book.ebook.addedToAbonnement,
      }));
      // Сбрасываем флаг после показа модалки
      thunkAPI.dispatch(readerActions.setIsRefreshAfterAuthLoading(false));
    } catch (error) {
      // Сбр��сываем флаг �� случае ошибки
      thunkAPI.dispatch(readerActions.setIsRefreshAfterAuthLoading(false));
      throw error;
    }
  },
);

export const sendGoalBuyBookToVkPixel = createAsyncThunk<void, { chapterId: number; }, {
  state: RootState
}>(
  `${bookReaderSliceName}/sendGoalBuyBookToVkPixel`,
  async (data, thunkAPI) => {
    try {
      const { currentBookProperties, currentGraphqlBook } = thunkAPI.getState().book;
      const chapters = ebookChaptersSelector(thunkAPI.getState());

      if (currentGraphqlBook?.ebook?.bought) {
        const firstPaidChapter = chapters[currentBookProperties.freeChaptersCount];

        if (firstPaidChapter && data.chapterId === Number(firstPaidChapter?.chapterId)) {
          const vkPixelResult = await campaignsApi.getPixel({ bookId: currentGraphqlBook.id });
          if ('data' in vkPixelResult && vkPixelResult.data?.vk) {
            const storage = getLocalStorageSafe();
            if (storage) {
              const buyBooks = JSON.parse(storage.getItem('sendGoalBuyBookToVkPixel')) ?? [];
              if (!buyBooks.includes(currentGraphqlBook.slug)) {
                // @ts-ignore
                window.VK.Retargeting.Init(vkPixelResult.data.vk);
                // @ts-ignore
                window.VK.Goal('purchase', { value: currentGraphqlBook.ebook.price / 2 });

                storage.setItem(
                  'sendGoalBuyBookToVkPixel',
                  JSON.stringify([...buyBooks, currentGraphqlBook.slug]),
                );
              }
            }
          }
        }
      }
    } catch (e) {
      return thunkAPI.rejectWithValue({ error: e.message });
    }
  },
);

export const savePlaceRead = createAsyncThunk<
void,
Omit<SavePlaceReadParams, 'bookId'> & { signal?: AbortSignal },
{ rejectValue: { error: string }, state: RootState }
>(
  `${bookReaderSliceName}/savePlaceRead`,
  async (
    data,
    thunkAPI,
  ) => {
    try {
      const { currentGraphqlBook } = thunkAPI.getState().book;

      thunkAPI.dispatch(readerActions.setIsSavePlaceReadLoading(true));

      let newChapterProgress = data.chapterProgress;

      // Нормализуем прогресс в диапазоне 0-100
      if (data.chapterProgress < 0) {
        newChapterProgress = 0;
      }

      if (data.chapterProgress > 99) {
        newChapterProgress = 100;
      }

      // Упрощенная логика без проверки на 15%
      // Всегда сохраняем, т.к. вызов контролируется сервисом синхронизации
      await myBooksApi.savePlaceRead({
        chapterId: data.chapterId,
        chapterProgress: newChapterProgress,
        bookId: currentGraphqlBook?.id,
      }, data.signal);

      thunkAPI.dispatch(readerActions.changeSaveChapterId(data.chapterId));
      thunkAPI.dispatch(readerActions.changeSaveChapterProgress(newChapterProgress));
    } catch (error) {
      // Если запрос отменен - это нормально, не показываем ошибку
      if (error.name === 'AbortError') {
        return;
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    } finally {
      thunkAPI.dispatch(readerActions.setIsSavePlaceReadLoading(false));
    }
  },
);

export const getPlaceReadByBookSlug = createAsyncThunk<
GetPlaceReadResult,
GetPlaceReadByBookSlugParams,
{ rejectValue: { error: string }; }
>(
  `${bookReaderSliceName}/getPlaceReadByBookSlug`,
  async (
    data,
    thunkAPI,
  ) => {
    try {
      const result = await myBooksApi.getPlaceReadByBookSlug(data);

      if (result && 'data' in result) {
        thunkAPI.dispatch(readerActions.changeSaveChapterId(result.data.chapterId));
        thunkAPI.dispatch(readerActions.changeSaveChapterProgress(result.data.chapterProgress));
        return result.data;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

export const getTextChapterInReader = createAsyncThunk<
void, { chapterId: string }, { state: RootState }
>(
  `${bookReaderSliceName}/getTextChapterInReader`,
  async (data, thunkAPI) => {
    const { isOpenDropMenu } = readerSelector(thunkAPI.getState());
    const { currentGraphqlBook } = thunkAPI.getState().book;
    const { loading } = thunkAPI.getState().chapter;

    try {
      // Устанавливаем loading только если он еще не установлен
      if (!loading) {
        thunkAPI.dispatch(readerActions.setLoading(true));
      }
      unwrapResult(await thunkAPI.dispatch(getReaderTextChapter({ chapterId: Number(data.chapterId), bookSlug: currentGraphqlBook?.slug })));

      // Статистика просмотра - некритичная операция, запускаем в фоне
      thunkAPI.dispatch(viewChapter({ chapterId: data.chapterId })).catch(() => {});

      thunkAPI.dispatch(readerActions.setLoading(false));
    } catch (error) {
      thunkAPI.dispatch(readerActions.setLoading(false));
    } finally {
      if (isOpenDropMenu) {
        thunkAPI.dispatch(readerActions.changeOpenDropMenu(false));
      }
    }
  },
);

export const checkChapterOnAvailable = createAsyncThunk<
void, { chapterId: string }, { state: RootState }
>(
  `${bookReaderSliceName}/checkChapterOnAvailable`,
  async (data, thunkAPI) => {
    const { isGetPublishChaptersLoading } = chapterSelector(thunkAPI.getState());
    const chapters = ebookChaptersSelector(thunkAPI.getState());
    const lastAvailableChapter = lastPublishAvailableChapterSelector(thunkAPI.getState());
    const isLoggedIn = isLoggedInSelector(thunkAPI.getState());
    const { loading: authLoading, providerCallbackFailed } = authSelector(thunkAPI.getState());
    const isAvailableChapter = chapters
      .find((chapter) => String(chapter.chapterId) === data.chapterId)?.available;

    // Не открываем модалку, если идёт авторизация через соцсеть или она только что завершилась с ошибкой
    const isProviderCallbackInProgress = authLoading.providerCallback === true;

    // Проверяем sessionStorage напрямую
    let providerCallbackFailedFromStorage = false;
    try {
      providerCallbackFailedFromStorage = sessionStorage.getItem('provider_callback_failed') === 'true';
    } catch {
      // ignore
    }

    // Проверяем URL на наличие параметров OAuth (code, provider)
    let isOAuthCallbackInUrl = false;
    if (environments.isClient) {
      const urlParams = new URLSearchParams(window.location.search);
      isOAuthCallbackInUrl = !!(urlParams.get('code') && urlParams.get('provider'));
    }

    // Проверяем, открыто ли уже любое окно авторизации, чтобы не плодить дубликаты
    const {
      isOpenAuthModalWithLoadSaveChapter,
      isOpenLoginModalWithLoadSaveChapter,
      isOpenRegisterModalWithLoadSaveChapter,
    } = readerSelector(thunkAPI.getState());
    const {
      isOpenQuickAuthModalWithChoosePayBook,
      isOpenQuickLoginModalWithChoosePayBook,
      isOpenQuickRegisterModalWithChoosePayBook,
    } = choosePaySelector(thunkAPI.getState());
    const isAnyAuthModalOpen = isOpenAuthModalWithLoadSaveChapter
      || isOpenLoginModalWithLoadSaveChapter
      || isOpenRegisterModalWithLoadSaveChapter
      || isOpenQuickAuthModalWithChoosePayBook
      || isOpenQuickLoginModalWithChoosePayBook
      || isOpenQuickRegisterModalWithChoosePayBook;

    const shouldSkipAuthModal = isProviderCallbackInProgress || providerCallbackFailed || providerCallbackFailedFromStorage || isOAuthCallbackInUrl || isAnyAuthModalOpen;

    if ((!isAvailableChapter) && !isGetPublishChaptersLoading && chapters?.length) {
      if (!lastAvailableChapter?.chapterId) {
        if (environments.isClient) {
          await Router.push('/404');
        }
        thunkAPI.dispatch(readerActions.setLoading(false));
      } else if (!isLoggedIn && !shouldSkipAuthModal) {
        thunkAPI.dispatch(readerActions.setIsOpenAuthModalWithLoadSaveChapter(true));
      }
    }
  },
);

export const getSettingsFromLocalStorage = createAsyncThunk<void>(
  `${bookReaderSliceName}/getSettingsFromLocalStorage`,
  (_, thunkAPI) => {
    if (!environments.isClient) return;
    const storage = getLocalStorageSafe();
    const localSettings = storage?.getItem(textSettingsName);
    if (localSettings) {
      try {
        if (localSettings) {
          thunkAPI.dispatch(readerActions.setSettings(JSON.parse(localSettings)));
          thunkAPI.dispatch(readerActions.setIsDefaultSettings(false));
        }
      } catch (error) {
        thunkAPI.dispatch(readerActions.setSettings(readerDefaultSettings));
      }
    }
    thunkAPI.dispatch(readerActions.setIsSettingsLoaded(true));
  },
);

export const changeReaderChapter = createAsyncThunk<
void, { chapter: Partial<GraphqlEChapter>; page?: string | number; }, { state: RootState }
>(
  `${bookReaderSliceName}/changeReaderChapter`,
  async (data, thunkAPI) => {
    const { currentGraphqlBook } = thunkAPI.getState().book;
    const { isSendOpenBuyBookFromLastFreeChapterEvent } = thunkAPI.getState().reader.reader;
    const isBookFormat = isBookFormatBookReaderSelector(thunkAPI.getState());
    const { currentChapter, loading } = thunkAPI.getState().chapter;

    if (data.chapter?.available === false) {
      if (data.chapter?.chapterId) {
        thunkAPI.dispatch(readerActions.changeClickedChapterId(data.chapter.chapterId));
      }
      thunkAPI.dispatch(readerActions.setIsSendOpenBuyBookFromLastFreeChapterEvent(true));
      if (!isSendOpenBuyBookFromLastFreeChapterEvent) {
        // Дедуп по книге: повторные заходы в платную главу той же книги не должны
        // задваивать конверсию в воронке (изначальная Redux-защита работала только в пределах сессии).
        ymOnceForBook('open-buy-book-from-last-free-chapter', currentGraphqlBook?.id);
        reachVkPixelGoalOnceForBook('open-first-paid-chapter', currentGraphqlBook?.id);
      }

      // Открываем модалку в зависимости от состояния авторизации
      // Но не открываем, если идёт обновление данных после авторизации (isRefreshAfterAuthLoading)
      // или есть skipAfterLogin в URL (после успешной авторизации)
      const { isRefreshAfterAuthLoading } = readerSelector(thunkAPI.getState());

      if (environments.isClient) {
        const urlParams = new URLSearchParams(window.location.search);
        const isSkipAfterLogin = urlParams.get('skipAfterLogin') === 'true';

        if (!isRefreshAfterAuthLoading && !isSkipAfterLogin) {
          const { isLoggedInSelector } = await import('~/feature/user/isLoggedInSelector');
          const { authSelector } = await import('~/feature/authorization/auth.selector');
          const isLoggedIn = isLoggedInSelector(thunkAPI.getState());
          const { authPreviously: authPreviouslyFromRedux } = authSelector(thunkAPI.getState());
          // Читаем authPreviously напрямую из localStorage, т.к. Redux state может ещё не успеть обновиться
          const storage = getLocalStorageSafe();
          const authPreviouslyFromStorage = storage?.getItem('auth_previously') === 'true';
          const authPreviously = authPreviouslyFromRedux || authPreviouslyFromStorage;

          // ВСЕГДА фиксируем payEntity = book, когда пользователь кликает "Следующая глава" на платную
          // Это гарантирует правильную цепочку после авторизации:
          // - authPreviously=true: Auth → afterLoginHandler → showBuyModal (т.к. payEntity будет НЕ пустой)
          // - authPreviously=false: BuyBookModal → Auth внутри → afterLoginHandler → ChoosePayModal (т.к. payEntity = book)
          const { choosePayActions } = await import('~/feature/finance/BuyingSteps/ChoosePay/choosePay.slice');
          const { PayEntitiesEnum } = await import('~/api/finance/financeApiTypes');
          thunkAPI.dispatch(choosePayActions.changePayEntity(PayEntitiesEnum.book));

          if (!isLoggedIn && authPreviously) {
            // Пользователь ранее был авторизован - открываем окно авторизации
            // После авторизации: buyBookAfterLogin → afterLoginHandler → showBuyModal (т.к. payEntity = book)
            thunkAPI.dispatch(readerActions.setIsOpenAuthModalWithLoadSaveChapter(true));
          } else if (!isLoggedIn) {
            // Пользователь никогда не авторизовывался - открываем сценарий покупки (внутри будет авторизация)
            // После авторизации: afterLoginHandler → ChoosePayModal (т.к. payEntity = book по умолчанию)
            const { openBuyBookModal } = await import('~/feature/book/buyBook/buyBook.data');
            thunkAPI.dispatch(openBuyBookModal());
          } else {
            // Пользователь авторизован - открываем окно покупки
            const { openBuyBookModal } = await import('~/feature/book/buyBook/buyBook.data');
            thunkAPI.dispatch(openBuyBookModal());
          }
        }
      }
      return;
    }

    if ((data.chapter?.chapterId === Number(currentChapter.chapterId)) || loading) {
      return;
    }

    // Устанавливаем флаг навигации, чтобы при загрузке новой главы позиция сбрасывалась на начало
    // (а не восстанавливалась из localStorage)
    thunkAPI.dispatch(readerActions.setIsChapterNavigation(true));

    // Устанавливаем состояние загрузки ДО скролла, чтобы показать лоадер
    thunkAPI.dispatch(readerActions.setLoading(true));

    // Сбрасываем скролл только в обычном режиме (не в книжном формате)
    if (!isBookFormat && window?.scrollTo) {
      window.scrollTo({ top: 0 });
    }

    if (isBookFormat && data.page) {
      await Router.replace(
        `${pagesConfigStore.readerChapter.url}?page=${data.page}`,
        `${pagesConfigStore.readerChapter.as(currentGraphqlBook?.slug, data.chapter.chapterId)}?page=${data.page}`,
        { shallow: true },
      );
    } else {
      await Router.replace(
        pagesConfigStore.readerChapter.url,
        pagesConfigStore.readerChapter.as(currentGraphqlBook?.slug, data.chapter.chapterId),
        { shallow: true },
      );
    }
  },
);

export const hideAddBookInMyBookMessage = createAsyncThunk<
void, { bookId: BookIdType }, { state: RootState }
>(
  `${bookReaderSliceName}/hideAddBookInMyBookMessage`,
  (data) => {
    const storage = getLocalStorageSafe();
    if (storage) {
      const bookIds = storage.getItem(bookIdsForAddMyBooksName)?.split(',') ?? [];

      storage.setItem(
        bookIdsForAddMyBooksName,
        [...bookIds, data.bookId].join(','),
      );
    }
  },
);

export const getHiddenAddBookInMyBookMessage = createAsyncThunk<
string[], void, { state: RootState }
>(
  `${bookReaderSliceName}/getHiddenAddBookInMyBookMessage`,
  () => {
    const storage = getLocalStorageSafe();
    if (storage) {
      const booksIds = storage.getItem(bookIdsForAddMyBooksName)?.split(',') ?? [];
      return booksIds;
    }
    return [];
  },
);

export const changeReaderPage = createAsyncThunk<
void, { page: string | number }, { state: RootState }
>(
  `${bookReaderSliceName}/changeReaderPage`,
  async (data) => {
    try {
      const currentUrl = qs.parseUrl(window.location.href);

      currentUrl.query.page = String(data.page);

      const url = new URL(qs.stringifyUrl(currentUrl));

      const redirectUrl = url.pathname + url.search + url.hash;

      await Router.replace(
        `${Router.pathname}?page=${String(data.page)}`,
        redirectUrl,
        { shallow: true },
      );
    } catch (error) {
      console.error('change reader page error ', error);
    }
  },
);

export const bookReaderNextChapter = createAsyncThunk<
void, void, { state: RootState }
>(
  `${bookReaderSliceName}/bookReaderNextChapter`,
  (data, thunkAPI) => {
    const {
      textSettings,
      openBookFormatPage,
      bookFormatCountPages,
    } = readerSelector(thunkAPI.getState());
    const { currentGraphqlBook } = bookSelector(thunkAPI.getState());
    const { currentChapter } = chapterSelector(thunkAPI.getState());
    const chapters = ebookChaptersSelector(thunkAPI.getState());
    const isFirstChapter = isFirstChapterInReaderSelector(currentChapter.chapterId)(thunkAPI.getState());
    const isSecondChapter = isSecondChapterInReaderSelector(currentChapter.chapterId)(thunkAPI.getState());
    const isLastBookFormatPage = isLastBookFormatPageSelector(thunkAPI.getState());
    const pageStep = bookFormatPageStepSelector(thunkAPI.getState());
    const currentChapterIndex = chapters
      .findIndex((chapter) => chapter.chapterId === Number(currentChapter.chapterId));
    const { isBookFormat } = textSettings;
    const nextChapter = chapters[currentChapterIndex + 1] ?? {} as Partial<GraphqlEChapter>;
    const newPage = openBookFormatPage + pageStep;
    const shouldTrackMoveFromFirstToSecondChapter = isFirstChapter && currentGraphqlBook?.ebook?.canBuy;
    const shouldTrackMoveFromSecondToThirdChapter = isSecondChapter && currentGraphqlBook?.ebook?.canBuy;
    const trackMoveToThirdChapter = () => {
      ymOnceForBook('reader-move-from-second-to-third-chapter', currentGraphqlBook?.id);
      reachVkPixelGoalOnceForBook('open-third-chapter', currentGraphqlBook?.id);
    };

    if (isBookFormat) {
      // Проверяем, выходит ли новая страница за пределы
      const wouldExceedPages = newPage >= bookFormatCountPages;

      if (isLastBookFormatPage || wouldExceedPages) {
        if (shouldTrackMoveFromFirstToSecondChapter) {
          ymOnceForBook('reader-move-from-first-to-second-chapter', currentGraphqlBook?.id);
          reachVkPixelGoalOnceForBook('open-second-chapter', currentGraphqlBook?.id);
        }
        if (shouldTrackMoveFromSecondToThirdChapter) trackMoveToThirdChapter();

        if (nextChapter && nextChapter.chapterId) {
          // НЕ сбрасываем openBookFormatPage здесь, чтобы избежать мерцания
          // Страница будет установлена в 0 при загрузке новой главы в ReaderTextChapter/ReaderTextBook
          thunkAPI.dispatch(changeReaderChapter({ chapter: nextChapter, page: 'first' }));
        }
        return;
      }

      thunkAPI.dispatch(changeReaderPage({ page: newPage }));
      return;
    }

    if (shouldTrackMoveFromFirstToSecondChapter) {
      ymOnceForBook('reader-move-from-first-to-second-chapter', currentGraphqlBook?.id);
      reachVkPixelGoalOnceForBook('open-second-chapter', currentGraphqlBook?.id);
    }
    if (shouldTrackMoveFromSecondToThirdChapter) trackMoveToThirdChapter();
    thunkAPI.dispatch(changeReaderChapter({ chapter: nextChapter }));
  },
);

export const bookReaderPrevChapter = createAsyncThunk<
void, void, { state: RootState }
>(
  `${bookReaderSliceName}/bookReaderPrevChapter`,
  (data, thunkAPI) => {
    const {
      textSettings,
      openBookFormatPage,
      bookFormatCountPages,
    } = readerSelector(thunkAPI.getState());
    const { currentChapter } = chapterSelector(thunkAPI.getState());
    const chapters = ebookChaptersSelector(thunkAPI.getState());
    const isFirstBookFormatPage = isFirstBookFormatPageSelector(thunkAPI.getState());
    const pageStep = bookFormatPageStepSelector(thunkAPI.getState());
    const currentChapterIndex = chapters
      .findIndex((chapter) => chapter.chapterId === Number(currentChapter.chapterId));
    const { isBookFormat } = textSettings;
    const prevChapter = chapters[currentChapterIndex - 1] ?? {} as Partial<GraphqlEChapter>;
    const nextPageCount = openBookFormatPage - pageStep;

    // Если в книжном формате, сначала клампим текущую страницу
    if (isBookFormat && bookFormatCountPages > 0) {
      const clampedPage = Math.min(Math.max(0, openBookFormatPage), bookFormatCountPages - 1);
      if (clampedPage !== openBookFormatPage) {
        thunkAPI.dispatch(readerActions.changeOpenBookFormatPage(clampedPage));
        return;
      }
    }

    if (isBookFormat && isFirstBookFormatPage) {
      // Проверяем, есть ли предыдущая глава
      if (prevChapter && prevChapter.chapterId) {
        thunkAPI.dispatch(changeReaderChapter({ chapter: prevChapter, page: 'last' }));
      }
      // Если предыдущей главы нет - ничего не делаем (уже на первой странице первой главы)
      return;
    }

    if (isBookFormat && nextPageCount === 0) {
      thunkAPI.dispatch(changeReaderPage({ page: 'first' }));
      return;
    }

    if (isBookFormat) {
      const newPage = Math.max(0, openBookFormatPage - pageStep);
      thunkAPI.dispatch(changeReaderPage({ page: newPage }));
      return;
    }

    thunkAPI.dispatch(changeReaderChapter({ chapter: prevChapter }));
  },
);

export const getAuthorBooksWithoutCurrentCycleBooks = createAsyncThunk<
void, void, { state: RootState }
>(
  `${bookReaderSliceName}/getAuthorBooksWithoutCurrentCycleBooks`,
  async (_, thunkAPI) => {
    try {
      const { currentGraphqlBook } = bookSelector(thunkAPI.getState());

      if (currentGraphqlBook && 'id' in currentGraphqlBook) {
        const result = await thunkAPI.dispatch(
          getCyclesByAuthorUsername({ authorUsername: currentGraphqlBook.author.username }),
        );

        if (getCyclesByAuthorUsername.fulfilled.match(result)) {
          thunkAPI.dispatch(readerActions.changeOtherAuthorBooksWithoutCurrentCycleBooks(
            result.payload.data.reduce((acc, cycle) => {
              if (
                Number(cycle.id) !== currentGraphqlBook.cycle.id
                || currentGraphqlBook.cycle.name === defaultCycleName
              ) {
                return [...acc, ...cycle.books];
              }

              return acc;
            }, []),
          ));
        }
      }
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);
